import React from "react";
import styled from "@emotion/styled";
import { css, keyframes } from "@emotion/react";

export type PlatoMarkVariant = "sparkle" | "sparkles" | "wand";

const STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

interface PlatoMarkProps {
  variant?: PlatoMarkVariant;
  size?: number;
  color?: string;
  spinning?: boolean;
}

function PlatoMark({ variant = "sparkle", size = 16, color = "currentColor", spinning = false }: PlatoMarkProps) {
  let inner: React.ReactNode;
  if (variant === "wand") {
    inner = (
      <g {...STROKE}>
        <path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72" />
        <path d="m14 7 3 3" /><path d="M5 6v4" /><path d="M19 14v4" /><path d="M10 2v2" /><path d="M7 8H3" /><path d="M21 16h-4" /><path d="M11 3H9" />
      </g>
    );
  } else if (variant === "sparkles") {
    inner = (
      <g>
        <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" fill="currentColor" />
        <circle cx="4" cy="20" r="2" fill="currentColor" />
        <path d="M20 2v4" {...STROKE} /><path d="M22 4h-4" {...STROKE} />
      </g>
    );
  } else {
    inner = (
      <g {...STROKE}>
        <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.582a.5.5 0 0 1 0 .962L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z" />
      </g>
    );
  }
  return (
    <Styled.Mark spinning={spinning} style={{ color }}>
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">{inner}</svg>
    </Styled.Mark>
  );
}

interface PlatoChipProps {
  size?: number;
  radius?: number;
  variant?: PlatoMarkVariant;
  spinning?: boolean;
}

function PlatoChip({ size = 26, radius, variant, spinning = false }: PlatoChipProps) {
  return (
    <Styled.Chip style={{ width: size, height: size, ...(radius ? { borderRadius: radius } : {}) }}>
      <PlatoMark size={Math.round(size * 0.62)} variant={variant} spinning={spinning} />
    </Styled.Chip>
  );
}

export { PlatoMark, PlatoChip };

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

Styled.Mark = styled.span((props: any) => css`
  label: PlatoMark;
  display: inline-flex;
  align-items: center;
  svg {
    display: block;
  }
  ${props.spinning &&
  css`
    animation: ${spin} 1.2s linear infinite;
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `}
`);

Styled.Chip = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoChip;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${t.color.black};
    background: linear-gradient(120deg, #fbd7ff 10%, #ffdec1 90%);
    ${t.rounded.md}
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.07);
  `;
});
